import { ReactElement } from 'react';

import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';

import { Translate, useTranslate } from '@dreryk/react-common';
import {
   IconSize,
   IconWrapper,
   Link,
   RotateRightIcon,
   StyledFlex,
   Text,
   TextSeparator,
   TextSeparatorType,
} from '@dreryk/react-components';

import {
   OnRefundSuccessCallback,
   RefundAdvancePaymentShowModalEventInterceptor,
} from '~libs.desktop.shared.refund-advance-payment';

import { visitSettlementTranslationKeys } from '../../../translations';
import { useMedicalServicesPrices } from '../../medicalServices/prices/MedicalServicesPrices.hooks';
import { isRefundingOnlinePaymentsAvailable } from '../../medicalServices/prices/MedicalServicesPrices.utils';
import { VisitDetailsFormBean } from '../visitDetails/visitDetails.types';
import { getAdvancePaymentMethodText } from '../../../../calendar/Timetable/shared/contextDialog/details/AppointmentAdvancePayments/AppointmentAdvancePaymentInfoItem.utils';

import { StyledPriceText, StyledText } from './AdvancePaymentTextView.styled';

const t = visitSettlementTranslationKeys.shared.form.sections;

type AdvancePaymentTextViewProps = {
   readonly advancePayments: FinancialSettlementAdvancePayment[];
   readonly medicalServicesPrices: number[];
   readonly visitDetails: VisitDetailsFormBean;
   readonly onRefundSuccess: OnRefundSuccessCallback;
};

export const AdvancePaymentTextView = (props: AdvancePaymentTextViewProps): ReactElement => {
   const translate = useTranslate();
   const { totalPriceAmount } = useMedicalServicesPrices({
      advancePayments: props.advancePayments,
      medicalServicesPrices: props.medicalServicesPrices,
   });

   return (
      <StyledFlex direction={'column'} gap={4}>
         <StyledText testId={'advancePaymentHeader'}>
            <Translate>{t.advancePayment.header}</Translate>
         </StyledText>
         <StyledFlex gap={4} direction={'column'}>
            {props.advancePayments.map((advancePayment) => (
               <StyledFlex alignItems={'center'} gap={12} key={advancePayment.initialTransactionId}>
                  <TextSeparator separator={TextSeparatorType.COMMA_SPACE}>
                     <StyledPriceText
                        testId={'advancePaymentAmount'}
                        value={advancePayment.remainingAmount.asNumber()}
                        stripDecimalZeroes
                     />
                     <Text testId={'advancePaymentMethod'}>
                        {getAdvancePaymentMethodText(advancePayment, translate)}
                     </Text>
                  </TextSeparator>

                  {isRefundingOnlinePaymentsAvailable(advancePayment.transactionExternalId) ? (
                     <RefundAdvancePaymentShowModalEventInterceptor
                        advancePayment={advancePayment}
                        beginDate={props.visitDetails.beginDate}
                        endDate={props.visitDetails.endDate}
                        patientFirstName={props.visitDetails.patient.firstName}
                        patientLastName={props.visitDetails.patient.lastName}
                        totalPriceAmount={totalPriceAmount}
                        onSuccess={props.onRefundSuccess}
                     >
                        <Link
                           icon={
                              <IconWrapper testId={'rotateRightIcon'} size={IconSize.SMALL}>
                                 <RotateRightIcon />
                              </IconWrapper>
                           }
                           testId={'refundAdvancePayment'}
                           label={t.advancePayment.actions.label}
                        />
                     </RefundAdvancePaymentShowModalEventInterceptor>
                  ) : null}
               </StyledFlex>
            ))}
         </StyledFlex>
      </StyledFlex>
   );
};
